import { useEffect, useState } from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { fmt, fmtDecimal, fmtPct, chartColor, youthPct, wingColor } from "@/lib/format";

const Stat = ({ label, value }) => (
    <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-lg font-semibold tabular text-foreground">{value}</p>
    </div>
);

// group breakdown rows to render in the detail table
const BREAKDOWN = [
    { code: "spiritual", label: "Spiritual" },
    { code: "education", label: "Education" },
    { code: "service", label: "Service" },
];

export const EntityDetailDialog = ({ entity, type = "district", open, onOpenChange }) => {
    const [trendMetric, setTrendMetric] = useState("Activities");
    useEffect(() => { setTrendMetric("Activities"); }, [entity?.samithi_id, entity?.district_id]);
    if (!entity) return null;
    const fy = entity.full_year;
    const m = entity.membership;
    const idx = fy.analytical_indices;
    const wd = fy.wing_details || {};

    const monthlyData = (entity.monthly_trend || []).map((item) => ({
        period: item.month_label || item.month || item.period_label,
        Activities: item.reported_activities || 0,
        Attendance: item.total_attendance || 0,
        Beneficiaries: item.beneficiaries || 0,
        Spiritual: item.wing_details?.spiritual?.reported_activities || 0,
        Education: item.wing_details?.education?.reported_activities || 0,
        Service: item.wing_details?.service?.reported_activities || 0,
    }));
    const trendData = monthlyData;
    const trendLabel = "Monthly activity trend (April–March)";

    const name = type === "district" ? entity.district_name : entity.samithi_name;
    const subtitle = type === "district"
        ? `${fmt(entity.units?.samithis)} samithis · ${fmt(entity.units?.bhajan_mandalis)} bhajan mandalis`
        : `${entity.district_name} district`;

    const ge = fy.gender_engagement || {};
    const memberGender = m.member_gender || m.gender || {};
    const gentsMembers = memberGender.gents_total ?? memberGender.gents ?? m.gents_total ?? m.approved_gents ?? null;
    const ladiesMembers = memberGender.ladies_total ?? memberGender.ladies ?? m.ladies_total ?? m.approved_ladies ?? null;
    const youthGender = m.youth_gender || {};
    const gentsYouth = youthGender.gents_youth ?? m.approved_gents_youth ?? null;
    const ladiesYouth = youthGender.ladies_youth ?? m.approved_ladies_youth ?? null;
    const gurus = m.bal_vikas_gurus ?? m.approved_balvikas_guru ?? entity.bal_vikas_compliance?.active_guru_count ?? null;
    const alumni = m.approved_alumni ?? m.approved_balvikas_alumni ?? m.bal_vikas_alumni ?? entity.bal_vikas_compliance?.approved_alumni ?? null;
    const displayCount = (value) => value === null || value === undefined ? "Not available" : fmt(value);

    // build breakdown rows including a Total row
    const rows = BREAKDOWN.map((b) => ({
        label: b.label,
        code: b.code,
        d: wd[b.code] || {},
    }));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{name}</DialogTitle>
                    <DialogDescription>{subtitle}</DialogDescription>
                </DialogHeader>

                <div>
                    <p className="mb-2 text-sm font-medium text-foreground">Membership and Bal Vikas profile</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <Stat label="Total members" value={fmt(m.approved_members)} />
                        <Stat label="Gents members" value={displayCount(gentsMembers)} />
                        <Stat label="Ladies members" value={displayCount(ladiesMembers)} />
                        <Stat label="Youth members" value={fmt(m.approved_youth_members)} />
                        <Stat label="Gents youth" value={displayCount(gentsYouth)} />
                        <Stat label="Ladies youth" value={displayCount(ladiesYouth)} />
                        <Stat label="Youth share" value={fmtPct(m.youth_share_pct)} />
                        <Stat label="Bal Vikas gurus" value={displayCount(gurus)} />
                        <Stat label="Bal Vikas alumni" value={displayCount(alumni)} />
                    </div>
                    {(gentsMembers === null || ladiesMembers === null || alumni === null) && (
                        <p className="mt-2 text-xs text-muted-foreground">
                            “Not available” indicates that the corresponding Samithi membership record could not be matched.
                        </p>
                    )}
                </div>

                <div>
                    <p className="mb-2 text-sm font-medium text-foreground">Activity performance</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <Stat label="Activities" value={fmt(fy.reported_activities)} />
                        <Stat label="Attendance" value={fmt(fy.total_attendance)} />
                        <Stat label="Avg / Activity" value={fmtDecimal(fy.average_attendance_per_activity)} />
                        <Stat label="Youth participation" value={fmtPct(youthPct(fy))} />
                        <Stat label="Beneficiaries" value={fmt(fy.beneficiaries)} />
                        <Stat label="Gents / Ladies participation" value={`${fmtPct(ge.gents_share_pct, 0)} / ${fmtPct(ge.ladies_share_pct, 0)}`} />
                    </div>
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                    <StatusBadge status={idx.youth_representation_index.status} label={`Youth: ${fmtDecimal(idx.youth_representation_index.value, 2)}`} />
                    <StatusBadge status={idx.wing_balance_index.status} label={`Balance: ${fmtDecimal(idx.wing_balance_index.value, 2)}`} />
                    <StatusBadge status={idx.programme_variety_score.status} label={`Variety: ${fmtDecimal(idx.programme_variety_score.value, 2)}`} />
                </div>

                {/* Group breakdown: Spiritual / Education / Service + Total */}
                <div className="mt-2">
                    <p className="mb-2 text-sm font-medium text-foreground">Breakdown by activity group</p>
                    <div className="overflow-x-auto rounded-lg border border-border/60">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border/70 hover:bg-transparent">
                                    <TableHead>Group</TableHead>
                                    <TableHead className="text-right">Activities</TableHead>
                                    <TableHead className="text-right">Attendance</TableHead>
                                    <TableHead className="text-right">Avg</TableHead>
                                    <TableHead className="text-right">Youth %</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.code} className="border-border/50">
                                        <TableCell>
                                            <span className="flex items-center gap-2 font-medium text-foreground">
                                                <span className="h-2.5 w-2.5 rounded-full" style={{ background: wingColor(r.code) }} />
                                                {r.label}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.d.reported_activities || 0)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.d.total_attendance || 0)}</TableCell>
                                        <TableCell className="text-right tabular">{fmtDecimal(r.d.average_attendance_per_activity || 0)}</TableCell>
                                        <TableCell className="text-right tabular">{(r.d.total_attendance || 0) > 0 ? fmtPct(youthPct(r.d)) : "—"}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="border-border/50 bg-secondary/40 font-semibold">
                                    <TableCell className="font-semibold text-foreground">Total</TableCell>
                                    <TableCell className="text-right tabular">{fmt(fy.reported_activities)}</TableCell>
                                    <TableCell className="text-right tabular">{fmt(fy.total_attendance)}</TableCell>
                                    <TableCell className="text-right tabular">{fmtDecimal(fy.average_attendance_per_activity)}</TableCell>
                                    <TableCell className="text-right tabular">{fmtPct(youthPct(fy))}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {trendData.length > 0 && (
                    <div className="mt-1 space-y-4">
                        <div>
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-medium text-foreground">{trendLabel}</p>
                                <div className="flex rounded-lg border border-border/70 bg-secondary/30 p-1">
                                    {["Activities", "Attendance", "Beneficiaries"].map((metric) => (
                                        <button
                                            key={metric}
                                            type="button"
                                            onClick={() => setTrendMetric(metric)}
                                            className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${trendMetric === metric ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                                        >
                                            {metric}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="h-[220px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={trendData} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                                        <CartesianGrid {...gridProps} />
                                        <XAxis dataKey="period" {...axisProps} interval={0} tick={{ fontSize: 10 }} />
                                        <YAxis {...axisProps} />
                                        <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                        {trendMetric === "Activities" ? (
                                            <Bar dataKey="Activities" fill={chartColor(1)} radius={[5, 5, 0, 0]} maxBarSize={32} />
                                        ) : (
                                            <Line type="monotone" dataKey={trendMetric} stroke={chartColor(3)} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                                        )}
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div>
                            <p className="mb-2 text-sm font-medium text-foreground">Monthly activity mix</p>
                            <div className="h-[190px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={trendData} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                                        <CartesianGrid {...gridProps} />
                                        <XAxis dataKey="period" {...axisProps} interval={0} tick={{ fontSize: 10 }} />
                                        <YAxis {...axisProps} />
                                        <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                        <Bar dataKey="Spiritual" stackId="mix" fill={wingColor("spiritual")} />
                                        <Bar dataKey="Education" stackId="mix" fill={wingColor("education")} />
                                        <Bar dataKey="Service" stackId="mix" fill={wingColor("service")} radius={[4, 4, 0, 0]} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-1 flex flex-wrap gap-4 text-xs text-muted-foreground">
                                {[['spiritual','Spiritual'],['education','Education'],['service','Service']].map(([code,label]) => (
                                    <span key={code} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: wingColor(code) }} />{label}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
